import type { InputHTMLAttributes, ReactNode } from "react";

import { cn } from "@/ui/cn";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  /** An icon drawn inside the field, before the text. */
  icon?: ReactNode;
}

/** A text field in the kit's look. Label it: wrap it in a <label> or pass aria-label. */
export function Input({ icon, className, ...rest }: Props) {
  return (
    <span className={cn("relative inline-flex w-full items-center", className)}>
      {icon ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 text-muted [&_svg]:size-4"
        >
          {icon}
        </span>
      ) : null}
      <input
        className={cn(
          "h-9 w-full rounded-lg border border-line-strong bg-surface pr-3 text-sm text-ink",
          "placeholder:text-muted focus:border-accent focus:outline-none",
          icon ? "pl-8" : "pl-3",
        )}
        {...rest}
      />
    </span>
  );
}

/** A native select in the kit's look; native so it works the same on a phone. */
export function Select({
  className,
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "h-9 rounded-lg border border-line-strong bg-surface px-2.5 text-sm text-ink",
        "focus:border-accent focus:outline-none",
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
}
